
import Vue from "vue";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { configStore, globalStore, uiStore } from "@/store/store";

import {
  maybeFilterColorVariable,
  filterVariablesByQuery,
  getGlobalVariableFromInputValue,
  getActiveElementOverrideVariables
} from "@/oxygen/variables/lib/utils";
import { getStyleForUnit } from "@/util/unit";
import { ColorPickerType } from "@/types/colors";
import { Variable } from "@/oxygen/variables/lib/types";

import Icon from "@/components/UI/Icon.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import SearchInput from "@/components/Reusable/CustomInputs/SearchInput.vue";
import ColorSwatch from "@/components/Reusable/CustomInputs/Color/ColorSwatch.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import VariableIcon from "@/oxygen/variables/VariableIcon.vue";
import VariableChooserList from "@/oxygen/variables/VariableChooserList.vue";
import VariableChooserEmpty from "@/oxygen/variables/VariableChooserEmpty.vue";
import SaveVariableDropdown from "@/oxygen/variables/SaveVariableDropdown.vue";
import { PanelContext } from "@/components/Builder/SidebarPanels.vue";

@Component({
  components: {
    SaveVariableDropdown,
    Icon,
    ColorSwatch,
    TextInput,
    SearchInput,
    Dropdown,
    DropdownInput,
    VariableIcon,
    VariableChooserEmpty,
    VariableChooserList
  }
})
export default class VariableChooser extends Vue {
  @Prop()
  path!: string;

  @Prop()
  name!: string;

  @Prop()
  value!: unknown;

  @Prop({ default: 10 })
  iconSize!: number;

  @Prop({ default: -4 })
  offset!: number;

  @Prop({ default: "" })
  type!: string;

  @Prop()
  colorType?: ColorPickerType;

  @Prop({ default: true })
  canCreate!: boolean;

  @Prop()
  variableToIgnore?: string;

  searchQuery = "";
  autofocus = false;
  isDialogOpen = false;
  isCreatingVariable = false;
  keepMaskOpenOnClose = false;

  @Inject({ from: "panelContext", default: undefined })
  readonly panelContext?: PanelContext;

  @Watch("isDialogOpen")
  onDialogOpenChange() {
    if (this.isDialogOpen) {
      this.keepMaskOpenOnClose = uiStore.panelState.oxyPanelIsMasked;

      uiStore.setPanelState({
        ...uiStore.panelState,
        oxyPanelIsMasked: true
      });

      setTimeout(() => {
        this.autofocus = true;
      }, 100);
    } else {
      this.autofocus = false;
      this.closeMask();
    }
  }

  get activeElement() {
    return uiStore.activeElement;
  }

  get isVariablesEnabled() {
    return configStore.builderMode === "oxygen";
  }

  get globalVariables() {
    return globalStore.variables
      .filter(item => item.type === this.type)
      .filter(item => maybeFilterColorVariable(item, this.colorType))
      .filter(item => {
        if (!this.variableToIgnore) return true;
        return item.id !== this.variableToIgnore;
      });
  }

  get overrideVariables() {
    return getActiveElementOverrideVariables();
  }

  get filteredVariables() {
    return filterVariablesByQuery(this.globalVariables, this.searchQuery);
  }

  get selectedVariable() {
    return getGlobalVariableFromInputValue(this.value);
  }

  detachVariable() {
    this.$emit("input", this.selectedVariable?.value || null);
    this.closeMask();

    this.isDialogOpen = false;
  }

  setVariable(globalVariable: Variable) {
    const name = `{var-${globalVariable.id}}`;

    if (this.type === "unit") {
      const unit = "custom";
      const style = getStyleForUnit(name, unit);

      this.$emit("input", {
        number: name,
        unit,
        style
      });
    } else {
      this.$emit("input", name);
    }

    this.closeMask();

    this.isDialogOpen = false;
  }

  position() {
    const panelPosition = this.panelContext?.position || "left";

    if (panelPosition === "right") {
      return {
        right: true,
        nudgeLeft: 20
      };
    }

    return {
      left: true,
      nudgeLeft: -20
    };
  }

  closeMask() {
    if (this.keepMaskOpenOnClose) {
      this.keepMaskOpenOnClose = false;
      return;
    }

    uiStore.setPanelState({
      ...uiStore.panelState,
      oxyPanelIsMasked: false
    });
  }
}
